import { faPlus } from "@fortawesome/free-solid-svg-icons/faPlus";
import { faTrash } from "@fortawesome/free-solid-svg-icons/faTrash";
import React from "react";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { Tooltip } from "../../../../utils/local-antd";

import { AtiraSelect } from "../../../../components/AtiraSelect";
import { Button } from "../../../../components/Button";
import { Flex } from "../../../../components/Flex";
import { Input } from "../../../../components/Input";
import { Text } from "../../../../components/Text";
import { TextWithTruncated } from "../../../../components/TextWithTruncated";
import { Modal } from "../../../../components/modal/Modal";
import { PermissionInviteMemberDto } from "../../../../model/permissions/dto/PermissionInviteMemberDto";
import { PermissionsInviteMembersDto } from "../../../../model/permissions/dto/PermissionsInviteMembersDto";
import {
  ASSIGNABLE_TEAM_ROLES,
  DEFAULT_INVITE_TEAM_ROLE,
  TEAM_ROLES,
} from "../../../../model/permissions/team-role.config";
import { TeamRole } from "../../../../model/permissions/types/TeamRole.enum";
import { permissionSliceSelectors } from "../../../../redux/permission/permission.selector";
import { permissionActions } from "../../../../redux/permission/permission.slice";
import { useAppDispatch, useAppSelector } from "../../../../redux/store";
import { userSliceSelectors } from "../../../../redux/user/user.selector";
import { TestIDs } from "../../../../tests/types/testIds";
import { Rounded } from "../../../../theme/Rounded";
import { Spacing } from "../../../../theme/Spacing";
import { TeamRoleBadge, TeamRoleLabel } from "../TeamRoleBadge";
import { AtiraToast } from "../../../../utils/AtiraToast";
import { Regexes } from "../../../../utils/String";
import { onSetTypedCRUDErrors } from "../../../../utils/onSetTypedCRUDErrors";

const AddButton = styled(Button)`
  margin: 0;
  padding: ${Spacing.s};
  height: 2.3rem;
  margin-bottom: auto;

  &:hover {
    background-color: ${({ theme }) => theme.lightMain};
  }
`;

const DeleteButton = styled(Button)`
  padding: ${Spacing.s};
  margin: 0;
`;

const SubmitButton = styled(Button)`
  width: 7rem;
  height: 2.3rem;
  margin: auto;

  &:hover {
    background-color: ${({ theme }) => theme.lightMain};
  }
`;

const EmailWrapper = styled(Flex)<{ valid: boolean }>`
  align-items: center;
  background-color: ${({ theme }) => theme.sub};
  border-radius: ${Rounded.md};
  justify-content: space-between;
  padding: ${Spacing.xs};
  border: ${({ theme, valid }) =>
    `1px solid ${theme[valid ? "sub" : "danger"]}`};
`;

const RequiredStart = styled.span`
  color: ${({ theme }) => theme.danger};
`;

interface MemberInviteModalProps {
  isOpen: boolean;
  onClose: VoidFunction;
}

export const MemberInviteModal: React.FC<MemberInviteModalProps> = ({
  isOpen = false,
  onClose,
}) => {
  const {
    control: controlEmail,
    handleSubmit: handleSubmitEmail,
    getValues: getValuesEmail,
    reset: resetEmail,
  } = useForm<PermissionInviteMemberDto>({
    defaultValues: { email: "", orgRole: DEFAULT_INVITE_TEAM_ROLE },
  });

  const {
    control,
    getValues,
    handleSubmit,
    reset,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm<PermissionsInviteMembersDto>({
    defaultValues: { members: [] },
  });
  const { append, fields, remove, replace } = useFieldArray({
    control,
    name: "members",
  });

  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const userId = useAppSelector(userSliceSelectors.selectLoggedInUserId)!;
  const loading = useAppSelector(permissionSliceSelectors.selectInviteLoading);

  const getDefaultActionsLabel = (role?: TeamRole) =>
    TEAM_ROLES.find(({ id }) => id === role)
      ?.defaultActions.map((action) => t(`common.${action.toLowerCase()}`))
      .join(", ");

  const onAddEmail = () => {
    append(getValuesEmail());
    resetEmail();
  };

  const onLocalClose = () => {
    onClose();
    remove();
    resetEmail();
  };

  const onRemoveEmail = (index: number) => {
    remove(index);
    clearErrors(`members.${index}`);
  };

  const onInvite = async () => {
    try {
      const { members } = getValues();
      await dispatch(
        permissionActions.inviteMembers({
          userId,
          members: members.map(({ email, orgRole }) => ({ email, orgRole })),
        }),
      ).unwrap();
      await dispatch(permissionActions.getInvitedMembers({ userId })).unwrap();
      AtiraToast.success(t("permissions.invites.invite_modal.success"));
      onClose();
      reset();
      resetEmail();
      replace([]);
    } catch (e: any) {
      console.error(e);
      onSetTypedCRUDErrors(e, setError);
      AtiraToast.apiError(e);
    }
  };

  return (
    <Modal
      open={isOpen}
      onClose={onLocalClose}
      title={t("permissions.invites.invite_modal.title")}
    >
      <Flex flexDirection="column">
        <Text color="darkerSub">
          {t("permissions.invites.invite_modal.description")}
        </Text>
        <Text color="darkerSub">
          {t("permissions.invites.invite_modal.description-2")}
        </Text>
      </Flex>

      <Flex marginTop="s" flexDirection="column" gap="m" width={"100%"}>
        <Flex flexDirection="column" gap="s">
          <Text>
            {t("permissions.invites.invite_modal.member_email")}
            <RequiredStart> *</RequiredStart>
          </Text>

          <Flex gap="s">
            <Controller
              control={controlEmail}
              name="email"
              rules={{
                required: {
                  value: true,
                  message: t("common.required"),
                },
                pattern: {
                  value: Regexes.EMAIL_REGEX,
                  message: t("common.invalid.email"),
                },
              }}
              render={({
                field: { onChange, value },
                fieldState: { error },
              }) => (
                <Input
                  placeholder="example@gmail.com"
                  required
                  type="email"
                  onChange={onChange}
                  value={value}
                  errorMessage={error?.message}
                  valid={!error}
                />
              )}
            />
            <Tooltip title={t("common.add")}>
              <AddButton
                onClick={handleSubmitEmail(onAddEmail, (e) => console.log(e))}
                icon={faPlus}
                iconWidth="lg"
                dataTestId={TestIDs.permissions.teamRole.inviteAdd}
              />
            </Tooltip>
          </Flex>
        </Flex>

        <Controller
          control={controlEmail}
          name="orgRole"
          render={({ field: { onChange, value } }) => (
            <Flex flexDirection="column" gap="xs">
              <AtiraSelect
                title={t("permissions.team-role.invite.label")}
                value={value}
                options={ASSIGNABLE_TEAM_ROLES.map(({ id }) => ({
                  label: <TeamRoleLabel role={id} />,
                  value: id,
                  "data-testid": TestIDs.permissions.teamRole.option(id),
                }))}
                onChange={onChange}
                className={TestIDs.permissions.teamRole.inviteSelect}
              />
              <Text fontSize="s" color="darkerSub">
                {t("permissions.team-role.starts-with", {
                  actions: getDefaultActionsLabel(value),
                })}
              </Text>
            </Flex>
          )}
        />

        <Flex flexDirection="column" gap="s">
          {fields.map((member, index) => {
            const errorMessage =
              errors?.members?.[index]?.email?.message ||
              errors?.members?.[index]?.orgRole?.message;

            return (
              <Flex key={member.id} flexDirection="column" gap="xs">
                <EmailWrapper valid={!errorMessage}>
                  <Flex alignItems="center" gap="s">
                    <TextWithTruncated length={40}>
                      {member.email}
                    </TextWithTruncated>
                    {member.orgRole ? (
                      <TeamRoleBadge role={member.orgRole} />
                    ) : null}
                  </Flex>

                  <Tooltip title={t("common.remove")}>
                    <DeleteButton
                      icon={faTrash}
                      onClick={() => onRemoveEmail(index)}
                    />
                  </Tooltip>
                </EmailWrapper>

                {errorMessage ? (
                  <div
                    data-testid={TestIDs.permissions.teamRole.inviteRowError(
                      index,
                    )}
                  >
                    <Text fontSize="s" color="danger">
                      {errorMessage}
                    </Text>
                  </div>
                ) : null}
              </Flex>
            );
          })}
        </Flex>

        <SubmitButton
          loading={loading}
          onClick={handleSubmit(onInvite)}
          disabled={!fields.length}
          dataTestId={TestIDs.permissions.teamRole.inviteSubmit}
        >
          {t("common.invite")}
        </SubmitButton>
      </Flex>
    </Modal>
  );
};
